import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { BulkAction, BulkOutcome, BulkPlan, BulkRow } from '@/hooks/useBulkOrderActions';

interface BulkActionDialogProps {
  action: BulkAction | null;
  plan: BulkPlan;
  outcome: BulkOutcome | null;
  failed: boolean;
  note: string;
  onNoteChange: (note: string) => void;
  isSubmitting: boolean;
  onSubmit: () => void;
  onClose: () => void;
}

interface RowListProps {
  title: string;
  rows: BulkRow[];
  tone: 'ok' | 'skip';
}

/** A titled list of orders; a row's reason is an i18n key or the server's own text. */
function RowList({ title, rows, tone }: RowListProps) {
  const { t } = useTranslation();

  return rows.length > 0 ? (
    <section aria-label={title} className='flex flex-col gap-1.5'>
      <h3 className='m-0 text-[12px] font-bold uppercase tracking-wide text-sd-muted-foreground'>
        {title} ({rows.length})
      </h3>
      <ul className='m-0 flex max-h-48 list-none flex-col gap-1 overflow-y-auto p-0'>
        {rows.map((row) => (
          <li key={row.uid} className='flex flex-wrap items-baseline gap-x-2 text-[13px]'>
            <span className='font-mono font-semibold text-sd-foreground'>{row.code}</span>
            {row.reason ? (
              <span
                className={tone === 'skip' ? 'text-sd-destructive' : 'text-sd-muted-foreground'}
              >
                {String(t(row.reason, { defaultValue: row.reason }))}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  ) : null;
}

/**
 * BOXY-376 — confirm which selected orders qualify (and why the others are
 * skipped), then show the server's per-order answer.
 */
export function BulkActionDialog({
  action,
  plan,
  outcome,
  failed,
  note,
  onNoteChange,
  isSubmitting,
  onSubmit,
  onClose,
}: BulkActionDialogProps) {
  const { t } = useTranslation();
  const isRto = action === 'rto';
  const title = isRto ? String(t('bulk_request_rto_title')) : String(t('bulk_resend_title'));

  return (
    <Dialog open={action !== null} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className='sd-scope sd-font max-w-lg'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {outcome
              ? String(t(isRto ? 'bulk_rto_result_hint' : 'bulk_resend_result_hint'))
              : String(t('bulk_confirm_hint'))}
          </DialogDescription>
        </DialogHeader>

        {outcome ? (
          <div className='flex flex-col gap-4'>
            <RowList
              title={String(t(isRto ? 'bulk_result_requested' : 'bulk_result_resent'))}
              rows={outcome.done}
              tone='ok'
            />
            <RowList title={String(t('bulk_result_not_done'))} rows={outcome.refused} tone='skip' />
          </div>
        ) : (
          <div className='flex flex-col gap-4'>
            <RowList title={String(t('bulk_will_be_sent'))} rows={plan.eligible} tone='ok' />
            <RowList title={String(t('bulk_skipped'))} rows={plan.skipped} tone='skip' />
            {isRto ? (
              <label className='flex flex-col gap-1.5 text-[13px] font-semibold text-sd-foreground'>
                {String(t('bulk_rto_note_label'))}
                <Textarea
                  value={note}
                  maxLength={1000}
                  placeholder={String(t('bulk_rto_note_placeholder'))}
                  onChange={(event) => onNoteChange(event.target.value)}
                />
              </label>
            ) : null}
            {failed ? (
              <p role='alert' className='m-0 text-[13px] text-sd-destructive'>
                {String(t('operation_failed_please_try_again'))}
              </p>
            ) : null}
          </div>
        )}

        <DialogFooter>
          {outcome ? (
            <Button onClick={onClose}>{String(t('close'))}</Button>
          ) : (
            <>
              <Button variant='outline' onClick={onClose}>
                {String(t('cancel'))}
              </Button>
              <Button onClick={onSubmit} disabled={isSubmitting || plan.eligible.length === 0}>
                {String(
                  t(isRto ? 'bulk_request_rto_confirm' : 'bulk_resend_confirm', {
                    count: plan.eligible.length,
                  }),
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkActionDialog;
